/**
 * Contacts — people in the Lead Command Center demo.
 *
 * List (name, organization, email, phone, open leads, last activity) and a
 * detail pane at /demo/lcc/contacts/:contactId with contact info, linked
 * organization, related leads, and safe Send Email / Call / Edit actions.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Mail, Pencil, Phone, Search, User, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { lccApi, type LccContactDetail, type LccLead } from '@/lib/lcc-api';
import {
  formatLccRelative,
  LccEmptyState,
  LccErrorCard,
  LccLoadingCard,
  LccPageHeader,
  LccStatusBadge,
} from './lcc-shared';
import { CallButton, EmailComposeDialog } from './lead-actions';
import { EditContactDialog } from './record-dialogs';

export function LccContactsView() {
  const { contactId } = useParams();
  if (contactId) return <ContactDetail contactId={contactId} />;
  return <ContactList />;
}

function ContactList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'contacts', search],
    queryFn: () => lccApi.contacts(search || undefined),
  });

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <LccPageHeader
        title="Contacts"
        blurb="The people behind your leads and organizations. Open one to see their leads and reach out safely."
      />
      <div className="relative max-w-[280px]">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts…"
          className="flex h-9 w-full rounded-md border border-input bg-background pl-8 pr-2.5 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      {isLoading && <LccLoadingCard />}
      {error != null && <LccErrorCard title="Couldn't load contacts" error={error} />}
      {!isLoading && error == null && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Organization</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Phone</th>
                  <th className="px-4 py-2.5 font-semibold">Open leads</th>
                  <th className="px-4 py-2.5 font-semibold">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.items ?? []).map((contact) => (
                  <tr
                    key={contact.id}
                    className="cursor-pointer transition-colors hover:bg-muted/40"
                    onClick={() => navigate(`/demo/lcc/contacts/${contact.id}`)}
                  >
                    <td className="px-4 py-2.5 font-medium">{contact.fullName}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {contact.organizationName ?? contact.companyName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{contact.email ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums text-muted-foreground">{contact.phone ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums">{contact.openLeadCount}</td>
                    <td className="px-4 py-2.5 text-muted-foreground" title={contact.lastActivityAt}>
                      {formatLccRelative(contact.lastActivityAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(data?.items ?? []).length === 0 && <LccEmptyState icon={Users} message="No contacts found." />}
        </Card>
      )}
    </div>
  );
}

function ContactDetail({ contactId }: { contactId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'contact-detail', contactId],
    queryFn: () => lccApi.contactDetail(contactId),
  });

  if (isLoading) return <LccLoadingCard />;
  if (error != null || !data) {
    return <LccErrorCard title="Couldn't load this contact" error={error ?? new Error('Not found')} />;
  }
  return <ContactDetailLoaded detail={data} />;
}

function ContactDetailLoaded({ detail }: { detail: LccContactDetail }) {
  const { contact, organization, leads } = detail;
  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  // Reuse the lead-centric safe actions by pointing them at the contact's
  // newest lead (or a synthetic lead-less shell when none exists yet).
  const actionLead: LccLead | undefined = leads[0]
    ? { ...leads[0], email: contact.email ?? leads[0].email, phone: contact.phone ?? leads[0].phone }
    : undefined;

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <Link
        to="/demo/lcc/contacts"
        className="inline-flex items-center gap-1 text-[12px] text-muted-foreground underline-offset-2 hover:underline"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden />
        Contacts
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-[20px] font-semibold leading-tight">
            <User className="h-5 w-5 text-primary" aria-hidden />
            {contact.fullName}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
            {contact.roleTitle && <span>{contact.roleTitle}</span>}
            {organization ? (
              <Link to={`/demo/lcc/organizations/${organization.id}`} className="underline-offset-2 hover:underline">
                {organization.name}
              </Link>
            ) : (
              contact.companyName && <span>{contact.companyName}</span>
            )}
            {contact.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3 w-3" aria-hidden />
                {contact.email}
              </span>
            )}
            {contact.phone && (
              <span className="inline-flex items-center gap-1 tabular-nums">
                <Phone className="h-3 w-3" aria-hidden />
                {contact.phone}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {actionLead && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                disabled={!(contact.email ?? actionLead.email)}
                onClick={() => setEmailOpen(true)}
              >
                <Mail className="h-3.5 w-3.5" aria-hidden />
                Send Email
              </Button>
              <CallButton lead={actionLead} label="Call Contact" />
            </>
          )}
          <Button type="button" size="sm" className="gap-1.5" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Edit Contact
          </Button>
        </div>
      </div>

      <Card className="space-y-2.5 p-4">
        <h2 className="text-[13px] font-semibold">
          Leads ({leads.length})
        </h2>
        {leads.length === 0 && (
          <p className="text-[12.5px] text-muted-foreground">
            No leads linked to this contact yet — link one from a lead's Edit Lead dialog.
          </p>
        )}
        <ul className="divide-y divide-border">
          {leads.map((lead) => (
            <li key={lead.id} className="flex flex-wrap items-center gap-2 py-2 text-[12.5px]">
              <Link to={`/demo/lcc/leads/${lead.id}`} className="min-w-0 flex-1 font-medium underline-offset-2 hover:underline">
                {lead.title}
              </Link>
              <LccStatusBadge status={lead.status} label={lead.statusLabel} />
              <span className="text-[11.5px] text-muted-foreground">{formatLccRelative(lead.createdAt)}</span>
            </li>
          ))}
        </ul>
      </Card>

      <EditContactDialog contact={contact} open={editOpen} onOpenChange={setEditOpen} />
      {actionLead && <EmailComposeDialog lead={actionLead} open={emailOpen} onOpenChange={setEmailOpen} />}
    </div>
  );
}
